import { defaultSelectedSubscriptionIds } from "@pages/about-products/about-products-filter-options";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { useEffect, useState } from "react";

/** The columns picked in the two about-products dropdowns */
type AboutProductsFilterSelection = {
  /** Ids of the subscription products whose columns are shown */
  subscriptionIds: string[];

  /** Identifiers of the bundles whose columns are shown */
  bundleIdentifiers: string[];
};

/** @returns the stored ids when they survived as a list of ids, otherwise the given defaults */
function restoreIds(storedIds: unknown, defaultIds: string[]): string[] {
  return Array.isArray(storedIds) &&
    storedIds.every((id) => typeof id === "string")
    ? storedIds
    : defaultIds;
}

/**
 * Keeps the selection of the two about-products dropdowns, restoring what was picked the last time
 * and storing every change, so that the page opens with the columns the user cares about.
 *
 * @returns the current selection and a function to update either dropdown
 */
export function useAboutProductsFilters(): [
  AboutProductsFilterSelection,
  (update: Partial<AboutProductsFilterSelection>) => void,
] {
  const [selection, setSelection] = useState<AboutProductsFilterSelection>(
    () => {
      const stored = LocalStorageUtils.getJSONItem<
        Partial<AboutProductsFilterSelection>
      >("about-products-filters");

      return {
        subscriptionIds: restoreIds(
          stored?.subscriptionIds,
          defaultSelectedSubscriptionIds
        ),
        bundleIdentifiers: restoreIds(stored?.bundleIdentifiers, []),
      };
    }
  );

  useEffect(() => {
    LocalStorageUtils.setJSONItem("about-products-filters", selection);
  }, [selection]);

  return [
    selection,
    (update) => setSelection((previous) => ({ ...previous, ...update })),
  ];
}
